import { useLayoutEffect, type RefObject } from 'react';
import type { CharacterId } from '../../../../domain/ids/ids';
import {
  RUNNER_PLAYER_SPRITE_HEIGHT_SVG,
  RUNNER_PLAYER_SPRITE_WIDTH_S,
  RUNNER_PLAYER_VISUAL_SCALE,
  RUNNER_REF_VIEWBOX_WIDTH,
} from '../../../../domain/runner/runnerConstants';
import type { RunnerLevel, RunnerPlayer } from '../../../../domain/runner/runnerTypes';
import {
  isRunnerShieldBoosterExpiringSoon,
  runnerSpriteVariant,
} from '../../../../domain/runner/runnerPlayer';
import type { LevelCanvasLayout } from './layout/runnerLevelLayout';
import { projectSvgPointInWorld } from './layout/runnerLevelLayout';
import { LEVEL_GAMEPLAY_Z_INDEX, PLAYER_TILT_DEGREES } from './gameplayConstants';
import {
  getLevelCharacterSprite,
  getLevelCharacterSpriteDisplayOffset,
  getLevelCharacterSpriteDisplayScale,
} from './characterLevelSprites';
import type { PlayfieldMotionBindings } from './playfieldMotionBindings';
import './LevelPlayerSprite.css';

type LevelPlayerSpriteProps = {
  level: RunnerLevel;
  player: RunnerPlayer;
  layout: LevelCanvasLayout;
  /** Наклон спрайта: -1..1 (доля от максимального угла). */
  tilt: number;
  characterId: CharacterId;
  /** Мигание после удара (hit-invulnerability). */
  isHitFlashing?: boolean;
  /** Пауза анимации мигания (pause / countdown). */
  isPaused?: boolean;
  motionBindings?: RefObject<PlayfieldMotionBindings | null>;
};

export function LevelPlayerSprite({
  level,
  player,
  layout,
  tilt,
  characterId,
  isHitFlashing = false,
  isPaused = false,
  motionBindings,
}: LevelPlayerSpriteProps) {
  const svgX = level.startPointSvgX + player.worldS;
  const svgY = (1 - player.lane) * level.viewBox.height;
  const { leftPx, topPx } = projectSvgPointInWorld(svgX, svgY, layout);
  const variant = runnerSpriteVariant(player);
  const isShieldExpiringSoon = isRunnerShieldBoosterExpiringSoon(player);
  const sprite = getLevelCharacterSprite(characterId, variant);
  const displayScale = getLevelCharacterSpriteDisplayScale(characterId, variant);
  const displayOffset = getLevelCharacterSpriteDisplayOffset(characterId);
  const scaleS = level.viewBox.width / RUNNER_REF_VIEWBOX_WIDTH;
  // Hitbox uses RUNNER_PLAYER_HITBOX_SCALE; drawn frame uses VISUAL_SCALE + speed art size.
  const widthPx =
    RUNNER_PLAYER_SPRITE_WIDTH_S *
    scaleS *
    layout.displayScale *
    RUNNER_PLAYER_VISUAL_SCALE *
    displayScale.scaleX;
  const heightPx =
    RUNNER_PLAYER_SPRITE_HEIGHT_SVG *
    scaleS *
    layout.displayScale *
    RUNNER_PLAYER_VISUAL_SCALE *
    displayScale.scaleY;
  const tiltDeg = -tilt * PLAYER_TILT_DEGREES;
  const innerClassName = [
    'level-player-sprite__inner',
    isHitFlashing ? 'level-player-sprite__inner--hit' : null,
    isHitFlashing && isPaused ? 'level-player-sprite__inner--hit-paused' : null,
  ]
    .filter(Boolean)
    .join(' ');

  // Seed pose once; RAF owns left/top/rotate thereafter (avoids stale React resets).
  useLayoutEffect(() => {
    const bindings = motionBindings?.current;
    if (bindings?.playerRoot != null) {
      bindings.playerRoot.style.left = `${leftPx}px`;
      bindings.playerRoot.style.top = `${topPx}px`;
    }
    if (bindings?.playerInner != null) {
      bindings.playerInner.style.transform = `rotate(${tiltDeg}deg)`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed only on mount/attempt
  }, [level.startPointSvgX, characterId, motionBindings]);

  return (
    <div
      ref={(el) => {
        if (motionBindings?.current != null) {
          motionBindings.current.playerRoot = el;
        }
      }}
      className="level-player-sprite"
      style={{
        position: 'absolute',
        width: widthPx,
        height: heightPx,
        transform: `translate(calc(-50% + ${displayOffset.xPct}%), calc(-50% + ${displayOffset.yPct}%))`,
        zIndex: LEVEL_GAMEPLAY_Z_INDEX.PLAYER,
      }}
    >
      <div
        ref={(el) => {
          if (motionBindings?.current != null) {
            motionBindings.current.playerInner = el;
          }
        }}
        className={innerClassName}
        style={{
          transformOrigin: '50% 50%',
        }}
      >
        {sprite.showShieldShine ? (
          <img
            className={[
              'level-player-sprite__shine',
              isShieldExpiringSoon ? 'level-player-sprite__shine--expiring' : null,
              isShieldExpiringSoon && isPaused
                ? 'level-player-sprite__shine--expiring-paused'
                : null,
            ]
              .filter(Boolean)
              .join(' ')}
            src={sprite.shieldShineSrc}
            alt=""
            draggable={false}
          />
        ) : null}
        <img
          className="level-player-sprite__character"
          src={sprite.characterSrc}
          alt=""
          draggable={false}
        />
      </div>
    </div>
  );
}
